import React from "react";
import { createPortal } from "react-dom";
import { marked } from "marked";
import PerFeatureAnalysisView, {
  FeatureColumnData,
  isPdfUrl,
  pageFromImageUrl,
  withPage,
} from "components/PerFeatureAnalysisView";
import ExpandableZoomableImage from "components/ExpandableZoomableImage";
import ZoomableImage from "components/ZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useAppState from "context/useAppState";
import ReAnalyzeModal from "components/common/modals/ReAnalyzeModal";
import ChangeVijilizeImageModal from "components/common/modals/ChangeVijilizeImageModal";
import {
  changePatentImage,
  copyImageForAnnotation,
  getMarkdownContent,
  getPatentVijilizedDetailsSlim,
  reanalyzeSingleClaimElement,
  updateCitationsForSingleComparison,
  updateReanalysisResult,
} from "services/project";
import {
  pfBuildEmphParts,
  pfBuildOwnChartHtml,
  pfCellHtml,
  pfFeatureCellHtml,
  pfChipCol,
  pfClaimHexEntries,
  pfClaimHexForSub,
  pfExtendChipPrefix,
  pfEmphFig,
  pfEscapeHtml,
  pfExtractAccusedUrl,
  pfExtractClause,
  pfExtractFigureInsight,
  pfHex,
  pfNorm,
  pfNormFig,
  pfOwnChartRows,
  pfParseChart,
} from "utils/pfMarkdown";

interface StripItem {
  it: any;
  idx: number;
  isMatched: boolean;
}

interface ClauseFeatureStripProps {
  items: StripItem[];
  comparisonId: string | null;
  projectId?: string | null;
  patentId: string;
  claimKey: string;
  /** Claim object of the claim view — legend fallback, keyed FIG_x. */
  claim: any;
  /** patent_dict — analyzer-built element profiles for the figure modal. */
  patentDict?: any;
  /** Source patent PDF link ("View Patent" beside the figures). */
  patentLink?: string;
  targetLink?: string;
  /** Column (annotation_id) to pre-select — return-from-view-more restore. */
  focusKey?: string | null;
  /** Opens the full View More Details page for a column's item (annotation /
   * reanalyze / change-image live there). preferredView picks the page's
   * initial mode — "classic" for the chart expand, default "graph". */
  onViewMore: (
    it: any,
    idx: number,
    isMatched: boolean,
    preferredView?: "graph" | "classic"
  ) => void;
  /** Opens the citations sidebar for a clicked [n] citation number. */
  onCitationClick?: (num: string) => void;
  /** Saves the return-restore context before an in-place navigation
   * (annotation editor) so coming back lands on the same clause/card. */
  onPersistContext?: (it: any, idx: number, isMatched: boolean) => void;
}

// [n] / [n,m] citation tokens → clickable sups — same conversion the View More
// page's walker effect does, but at HTML-string level.
const citeHtml = (html: string) =>
  (html || "").replace(
    /\[\[(\d+(?:\s*,\s*\d+)*)\]\]|\[(\d+(?:\s*,\s*\d+)*)\]/g,
    (_m, a, b) =>
      (a || b)
        .split(",")
        .map((n: string) => n.trim())
        .sort((x: string, y: string) => Number(x) - Number(y))
        .map(
          (n: string, i: number, arr: string[]) =>
            `<sup><span class="citation-link walkthrough-citation-sup" data-citation-number="${n}" style="color:#1751D0;cursor:pointer;">${n}</span></sup>${
              i < arr.length - 1 ? "<sup>,</sup>" : ""
            }`
        )
        .join("")
  );

// Presigned sibling data survives clause collapse/expand and target toggling.
const stripCache: Record<string, any> = {};

/**
 * Inline per-clause feature strip for the claim view's Features View — the
 * same columns (patent figure vs accused image, charts, analysis) as the
 * View More Details page, assembled from each item's own stored markdown.
 */
const ClauseFeatureStrip: React.FC<ClauseFeatureStripProps> = ({
  items,
  comparisonId,
  projectId,
  patentId,
  claimKey,
  claim,
  patentDict,
  patentLink,
  targetLink,
  focusKey,
  onViewMore,
  onCitationClick,
  onPersistContext,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [columns, setColumns] = React.useState<FeatureColumnData[]>([]);
  const [clauseHtml, setClauseHtml] = React.useState("");
  // Structured common chart so the Patent Figure / Accused Image columns can
  // render React image components (zoom + expand + rotate) per row.
  const [commonChart, setCommonChart] = React.useState<{
    header: string[];
    rows: { colKey: string; cellsHtml: string[]; hideImages?: boolean }[];
  } | null>(null);
  const [currentKey, setCurrentKey] = React.useState<string | null>(null);
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
  const colItemsRef = React.useRef<Record<string, StripItem>>({});
  // Hover preview + click modal for boxed feature mentions in the chart
  const [hoverPreview, setHoverPreview] = React.useState<{
    key: string;
    left: number;
    top: number;
    above: boolean;
  } | null>(null);
  const [figureModalKey, setFigureModalKey] = React.useState<string | null>(
    null
  );
  // In-place ReAnalyze / Change Image — modals + API run right here instead
  // of deep-linking into the View More Details page.
  const [reanalyzeKey, setReanalyzeKey] = React.useState<string | null>(null);
  const [showReanalyze, setShowReanalyze] = React.useState(false);
  const [changeImageKey, setChangeImageKey] = React.useState<string | null>(
    null
  );
  const [showChangeImage, setShowChangeImage] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const busyRef = React.useRef(false);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const navigate = useNavigate();
  // Seed the annotation editor's context directly — no view-more stopover.
  const {
    setAnnotationData,
    setAnnotationS3Path,
    setImageCoordinates,
    setMarkdownContent,
    setMarkdownS3Path,
    setOriginalImagePath,
    setPatentImage,
    setFontInfo,
  } = useAppState("vijilize");
  // Post-reanalysis field overrides — the parent's slim items stay stale
  // until the page refetches, so patch them when rebuilding columns.
  const overridesRef = React.useRef<Record<string, any>>({});

  React.useEffect(() => {
    const clear = () => setHoverPreview(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, []);

  React.useEffect(() => {
    if (!figureModalKey) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setFigureModalKey(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [figureModalKey]);

  // Same matching as ClaimElement.profilesFor: normalized patent_dict keys
  // matched against the feature text, keeping only the most specific.
  const profilesForFeature = (description: string) => {
    if (!patentDict || typeof patentDict !== "object") return [];
    const norm = (s: string) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const normDesc = norm(description);
    if (!normDesc) return [];
    const matched = Object.entries(patentDict)
      .filter(
        ([key, value]) =>
          typeof value === "string" &&
          (value as string).trim() &&
          norm(key) &&
          normDesc.includes(norm(key))
      )
      .sort(([a], [b]) => norm(b).length - norm(a).length);
    const kept: [string, any][] = [];
    matched.forEach(([key, value]) => {
      if (!kept.some(([k]) => norm(k).includes(norm(key))))
        kept.push([key, value]);
    });
    return kept.map(([key, value]) => ({
      name: key.replace(/_/g, " "),
      text: String(value).trim().replace(/^"|"$/g, ""),
    }));
  };

  // Figure → legend rows from the claim structure (items' own figure_legend
  // arrays are empty in practice — the real legends live on the claim).
  const claimLegendMap = React.useMemo(() => {
    const map: Record<string, { [k: string]: string }[]> = {};
    const add = (legends: any) => {
      (Array.isArray(legends) ? legends : []).forEach((l: any) => {
        const k = Object.keys(l || {})[0];
        if (k && Array.isArray(l[k]) && !map[pfNormFig(k)]) {
          map[pfNormFig(k)] = l[k];
        }
      });
    };
    add(claim?.legends);
    (claim?.elements || []).forEach((el: any) => {
      add(el?.legends);
      (el?.features || []).forEach((f: any) => add(f?.legends));
    });
    return map;
  }, [claim]);

  // Parent re-renders rebuild the items array each time — key the effect on a
  // stable signature so it only refetches when the actual items change.
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const itemsSig = items
    .map((e) => e.it?.annotation_id || `${e.isMatched ? "m" : "n"}-${e.idx}`)
    .join("|");

  React.useEffect(() => {
    let cancelled = false;
    const usable = itemsRef.current
      .map((e) => {
        const ov =
          e.it && typeof e.it === "object" && e.it.annotation_id
            ? overridesRef.current[e.it.annotation_id]
            : undefined;
        return ov ? { ...e, it: { ...e.it, ...ov } } : e;
      })
      .filter(
        (e) =>
          e.it &&
          typeof e.it === "object" &&
          (e.it.sub_feature || e.it.is_design_patent)
      );
    if (usable.length === 0) {
      setColumns([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    (async () => {
      // Each column pulls that item's OWN stored analysis; the markdown
      // endpoint presigns its patent figure + markdown URLs.
      const enriched = await Promise.all(
        usable.map(async (e) => {
          let md: string = e.it.markdown_content || "";
          let figureUrl = "";
          let figureNumber = e.it.patent_image?.figure_number || "";
          let figurePageNumber =
            e.it.patent_image?.figure_page_number ?? null;
          let figureLegend = e.it.patent_image?.figure_legend || [];
          if (e.it.annotation_id && comparisonId) {
            try {
              let data = stripCache[e.it.annotation_id];
              if (!data) {
                const res: any = await getMarkdownContent(
                  comparisonId,
                  patentId,
                  claimKey,
                  e.isMatched,
                  e.it.annotation_id
                );
                if (res?.success && res?.data) {
                  data = res.data;
                  stripCache[e.it.annotation_id] = data;
                }
              }
              if (data) {
                // getMarkdownContent returns camelCase markdownContent
                md = data.markdownContent || data.markdown_content || md;
                figureUrl = data.patentImage?.patent_figure_path || "";
                figureNumber = data.patentImage?.figure_number || figureNumber;
                figurePageNumber =
                  data.patentImage?.figure_page_number ?? figurePageNumber;
                figureLegend = data.patentImage?.figure_legend || figureLegend;
              }
            } catch (err) {
              console.error("Error fetching clause feature data:", err);
            }
          }
          if (
            !figureUrl &&
            typeof e.it.patent_image?.patent_figure_path === "string" &&
            e.it.patent_image.patent_figure_path.startsWith("https://")
          ) {
            figureUrl = e.it.patent_image.patent_figure_path;
          }
          return { ...e, md, figureUrl, figureNumber, figurePageNumber, figureLegend };
        })
      );
      if (cancelled) return;

      const clause = pfExtractClause(enriched.find((x) => x.md)?.md || "");
      const clausePos = (sf: string) => {
        const i = clause.toLowerCase().indexOf(String(sf || "").toLowerCase());
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
      };
      enriched.sort(
        (a, b) => clausePos(a.it.sub_feature) - clausePos(b.it.sub_feature)
      );

      const emphParts = pfBuildEmphParts(clause);
      const keyOf = (e: StripItem) =>
        e.it.annotation_id || `${e.isMatched ? "m" : "n"}-${e.idx}`;

      // Claim-structure hexes take precedence — indexing and vijilization
      // assign colors independently and can disagree (prod-observed).
      const claimHexEntries = pfClaimHexEntries(claim);

      const legendFor = (itemLegend: any, figureNumber: any) =>
        Array.isArray(itemLegend) && itemLegend.length > 0
          ? itemLegend
          : claimLegendMap[pfNormFig(figureNumber)];

      colItemsRef.current = {};
      const cols: FeatureColumnData[] = enriched.map((e) => {
        const key = keyOf(e);
        colItemsRef.current[key] = e;
        // Design items have no sub_feature — the figure label is the column.
        const isDesign = !!e.it.is_design_patent;
        const label = String(e.it.sub_feature || e.it.text || "");
        const hex =
          pfClaimHexForSub(e.it.sub_feature, claimHexEntries) ||
          pfHex(e.it.element_hex_code) ||
          undefined;
        // Design item text IS the figure label, not analysis prose.
        let analysisHtml = "";
        if (!isDesign) {
          try {
            analysisHtml = marked.parse(
              pfEmphFig(emphParts(pfEscapeHtml(String(e.it.text || ""))))
            ) as string;
          } catch {}
        }
        let figureInsightHtml = "";
        const fi = pfExtractFigureInsight(e.md);
        if (fi) {
          try {
            figureInsightHtml = marked.parse(pfEmphFig(emphParts(fi))) as string;
          } catch {}
        }
        return {
          key,
          subFeature: label,
          isMatched: e.isMatched,
          isCurrent: false,
          hexCode: hex,
          patentFigureUrl: e.figureUrl || undefined,
          figureNumber: e.figureNumber || undefined,
          figurePageNumber: e.figurePageNumber ?? undefined,
          figureLegend: legendFor(e.figureLegend, e.figureNumber),
          accusedImageUrl: pfExtractAccusedUrl(e.md) || undefined,
          chartHtml:
            citeHtml(
              pfBuildOwnChartHtml(e.md, label, emphParts, key, hex, isDesign)
            ) || undefined,
          analysisHtml: citeHtml(analysisHtml) || undefined,
          figureInsightHtml: citeHtml(figureInsightHtml) || undefined,
          matchPercentage: e.it.match_percentage,
        };
      });

      // Common claim chart: ONE table for the whole clause — each feature's
      // row(s) pulled from that feature's OWN item's chart. Structured (not
      // HTML) so image columns can be injected at render time.
      let common: {
        header: string[];
        rows: { colKey: string; cellsHtml: string[]; hideImages?: boolean }[];
      } | null = null;
      const headerSource = enriched.find((e) => pfParseChart(e.md));
      // Design headers are figure-scoped ("Patent Element (FIG. 1 & 2)") — the
      // shared table mixes figures, so drop the parenthetical scope.
      const allDesign = enriched.every((e) => !!e.it.is_design_patent);
      const commonHeader = headerSource
        ? pfParseChart(headerSource.md)!.header.map((h) =>
            allDesign ? h.replace(/\s*\([^)]*\)/g, "").trim() : h
          )
        : null;
      if (commonHeader) {
        const analysisIdx = commonHeader.findIndex((h) => /analysis/i.test(h));
        // Canonical sub-feature name in the Feature column — generated rows
        // sometimes scope the label to one numeral ("satellite body(602)").
        const featureIdx = Math.max(
          commonHeader.findIndex((h) => /^\s*feature\s*:?\s*$/i.test(h)),
          0
        );
        const rowsOut: {
          colKey: string;
          cellsHtml: string[];
          hideImages?: boolean;
        }[] = [];
        enriched.forEach((e) => {
          const isDesign = !!e.it.is_design_patent;
          const label = String(e.it.sub_feature || e.it.text || "");
          const chart = pfParseChart(e.md);
          // Design charts already hold one row per ornamental feature — keep all.
          let rows = chart
            ? isDesign
              ? chart.rows
              : pfOwnChartRows(chart, e.it.sub_feature)
            : [];
          if (rows.length === 0) {
            rows = [
              [label, ...Array(Math.max(commonHeader.length - 1, 0)).fill("—")],
            ];
          }
          const insight = pfExtractFigureInsight(e.md);
          const eCol = cols.find((c) => c.key === keyOf(e));
          rows.forEach((r, ri) => {
            rowsOut.push({
              colKey: keyOf(e),
              hideImages: isDesign && ri > 0,
              cellsHtml: Array.from({ length: commonHeader.length }, (_, ci) =>
                !isDesign && ci === featureIdx
                  ? pfFeatureCellHtml(label, keyOf(e), eCol?.hexCode)
                  : citeHtml(
                      pfCellHtml(
                        !isDesign && ci === analysisIdx && insight
                          ? insight
                          : r[ci] ?? "—",
                        emphParts
                      )
                    )
              ),
            });
          });
        });
        common = { header: commonHeader, rows: rowsOut };
      }

      setColumns(cols);
      setClauseHtml("");
      setCommonChart(common);
      setCurrentKey(
        (focusKey && cols.find((c) => c.key === focusKey)?.key) ||
          cols[0]?.key ||
          null
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSig, comparisonId, patentId, claimKey, claimLegendMap, refreshTick]);

  const openViewMoreFor = (key: string, view?: "graph" | "classic") => {
    const e = colItemsRef.current[key];
    if (e) onViewMore(e.it, e.idx, e.isMatched, view);
  };

  const toS3 = (u?: string) => {
    if (!u) return "";
    if (u.startsWith("s3://")) return u;
    if (!u.startsWith("https://")) return "";
    try {
      const url = new URL(u);
      return `s3://${url.hostname.split(".")[0]}/${url.pathname.substring(1)}`;
    } catch {
      return "";
    }
  };

  // Everything the modals/payloads need about one column's item.
  const ctxFor = (key: string | null) => {
    const e = key ? colItemsRef.current[key] : undefined;
    const data: any =
      e?.it?.annotation_id != null ? stripCache[e.it.annotation_id] : undefined;
    const srcFigS3 =
      data?.patentFigureS3Path ||
      toS3((e as any)?.figureUrl) ||
      toS3(e?.it?.patent_image?.patent_figure_path);
    const originalImagePath =
      data?.originalImagePath || e?.it?.original_image_path || "";
    return {
      e,
      data,
      srcFigS3,
      originalImagePath,
      sourcePatentId:
        (String(srcFigS3).match(/\/([^\/]+)\/[^\/]+\/figures\//) || [])[1] ||
        patentId,
      targetDocId:
        (String(originalImagePath).match(/\/([^\/]+)\/[^\/]+\/file\//) ||
          [])[1] || undefined,
    };
  };

  const teamIdFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem("selectedTeam") as string)
        ?.currentTeamId;
    } catch {
      return undefined;
    }
  };

  // Same chain as the View More reanalyze handler: Python analysis →
  // citations refresh → persist — but in place, no navigation.
  const runReanalysis = async (
    key: string,
    instruction: string,
    newImageUrl?: string
  ) => {
    if (busyRef.current) return;
    const { e, srcFigS3, originalImagePath, sourcePatentId } = ctxFor(key);
    const teamId = teamIdFromStorage();
    if (!e || !comparisonId || !projectId || !teamId) {
      toast.error("Missing project or team information.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    toast.success("Reanalysis in progress… please wait a moment.");
    try {
      const md = (e as any).md || "";
      const clauseMatch = md.match(/####\s*Feature:\s*\n(.+?)\n/);
      const clause = clauseMatch
        ? clauseMatch[1].trim()
        : String(e.it.sub_feature || "");
      const pre = claim?.claim_preamble || claim?.preamble;
      const claimPreambleText = Array.isArray(pre)
        ? pre.map((p: any) => p?.description || p).join(" ")
        : pre
        ? String(pre)
        : "";
      const targetImagePath = newImageUrl || originalImagePath || "";
      const targetPatentIdVal =
        (String(targetImagePath).match(/\/([^\/]+)\/[^\/]+\/file\//) ||
          [])[1] || patentId;

      const response: any = await reanalyzeSingleClaimElement({
        sourcePatentId,
        targetPatentId: targetPatentIdVal,
        claimElement: clause,
        sourcePatentFigureS3Path: srcFigS3,
        targetPatentFigureS3Path: targetImagePath,
        projectId,
        teamId,
        instruction: instruction || undefined,
        claimPreamble: claimPreambleText,
        claimFeature: String(e.it.sub_feature || ""),
      });
      const resultData = response?.result || response;
      if (!resultData) throw new Error("Empty reanalysis response");
      const annotationIdToUse =
        resultData.annotation_id ||
        response?.annotation_id ||
        e.it.annotation_id ||
        undefined;

      let analysis = resultData.analysis || "";
      let citations = resultData.citations || [];
      try {
        const cit: any = await updateCitationsForSingleComparison({
          index_name: teamId,
          result: {
            analysis,
            is_match: resultData.is_match,
            match_percentage: resultData.match_percentage,
            markdown_content: resultData.markdown_content || "",
            markdown_image: resultData.original_image_path || "",
            original_image_path: resultData.original_image_path || "",
            image_coordinates: resultData.image_coordinates || [],
            annotation_id: annotationIdToUse,
            is_system_generated: resultData.is_system_generated,
            patent_figure_path: resultData.patent_figure_path || "",
            figure_legend: resultData.figure_legend || [],
            figure_number: resultData.figure_number || "",
            features: resultData.features || [],
            citations,
            total_cost: resultData.total_cost,
            hex_code: resultData.hex_code || "#FF0000",
          },
          team_id: teamId,
          vijilize_id: comparisonId,
        });
        if (cit?.updated_analysis) analysis = cit.updated_analysis;
        if (cit?.new_citations) citations = cit.new_citations;
      } catch (err) {
        console.error("Citations step failed — keeping original analysis:", err);
      }

      await updateReanalysisResult({
        annotationId: annotationIdToUse,
        comparisonId,
        patentId,
        claimKey,
        isMatchedItem: e.isMatched,
        markdownContent: resultData.markdown_content || "",
        analysis,
        originalImagePath: resultData.original_image_path || "",
        patentFigurePath: resultData.patent_figure_path || "",
        figureNumber: resultData.figure_number || "",
        figurePageNumber: resultData.figure_page_number ?? null,
        isMatch: resultData.is_match,
        matchPercentage: resultData.match_percentage,
        citations,
        features: resultData.features || [],
        figureLegend: resultData.figure_legend || [],
        hexCode: resultData.hex_code || "#FF0000",
        isSystemGenerated: resultData.is_system_generated,
        totalCost: resultData.total_cost,
        imageCoordinates: resultData.image_coordinates || [],
        projectId,
        teamId,
        clickedItemIndex: e.idx,
      });

      // Rebuild this column from the fresh stored markdown.
      if (e.it.annotation_id) {
        delete stripCache[e.it.annotation_id];
        overridesRef.current[e.it.annotation_id] = {
          text: analysis,
          match_percentage: resultData.match_percentage,
          is_match: resultData.is_match,
        };
      }
      setRefreshTick((p) => p + 1);
      // Refresh the shared slim-payload cache in the background so later
      // navigation (heatmap/claim page remounts) sees the updated item.
      getPatentVijilizedDetailsSlim(comparisonId, true).catch(() => {});
      toast.success("Reanalysis completed");
    } catch (error) {
      console.error("Error during reanalysis:", error);
      toast.error("ReAnalyze failed. The original analysis is unchanged.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // Same persist+preload pattern as the View More change-image handler.
  const runChangeImage = async (
    key: string,
    newImageUrl: string,
    previewUrl?: string
  ) => {
    if (busyRef.current) return;
    const { e } = ctxFor(key);
    if (!e || !comparisonId) return;
    const displayUrl = previewUrl || newImageUrl;
    busyRef.current = true;
    setBusy(true);
    const preload = new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = displayUrl;
    });
    try {
      await Promise.all([
        changePatentImage({
          comparison_id: comparisonId,
          patent_figure_s3_path: newImageUrl,
          annotation_id: e.it.annotation_id,
          patent_id: patentId,
          claim_key: claimKey,
        }),
        preload,
      ]);
      setColumns((prev) =>
        prev.map((c) =>
          c.key === key ? { ...c, patentFigureUrl: displayUrl } : c
        )
      );
      if (e.it.annotation_id && stripCache[e.it.annotation_id]) {
        const cached = stripCache[e.it.annotation_id];
        stripCache[e.it.annotation_id] = {
          ...cached,
          patentImage: {
            ...(cached.patentImage || {}),
            patent_figure_path: displayUrl,
          },
          patentFigureS3Path: newImageUrl,
        };
      }
      colItemsRef.current[key] = { ...(e as any), figureUrl: displayUrl };
      toast.success("Patent Image Changed");
    } catch (error) {
      console.error("Error persisting patent image change:", error);
      toast.error("Failed to save patent image. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // Stage the target image and jump STRAIGHT into the annotation editor,
  // seeding the same vijilize app state the view-more page would have set.
  const runAnnotation = async (key: string) => {
    if (busyRef.current) return;
    const { e, data, originalImagePath } = ctxFor(key);
    const teamId = teamIdFromStorage();
    if (!e || !comparisonId || !projectId || !teamId) {
      toast.error("Missing project or team information.");
      return;
    }
    // The editor's save needs markdownS3Path — without it, use view-more.
    if (!data?.markdownS3Path || !originalImagePath) {
      openViewMoreFor(key);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      const res: any = await copyImageForAnnotation(
        originalImagePath,
        projectId,
        teamId
      );
      const annPath = res?.data?.annotation_s3_path;
      if (!annPath) {
        toast.error("Failed to prepare image for annotation");
        return;
      }
      setAnnotationData({
        patentId,
        claimKey,
        annotationId: e.it.annotation_id,
        isMatchedItem: e.isMatched,
      });
      setMarkdownContent(data.markdownContent || data.markdown_content || "");
      setImageCoordinates(data.imageCoordinates || null);
      setOriginalImagePath(originalImagePath);
      setMarkdownS3Path(data.markdownS3Path);
      setAnnotationS3Path(annPath);
      if (data.patentImage) setPatentImage(data.patentImage);
      if (data.fontInfo) setFontInfo(data.fontInfo);
      // The saved annotation rewrites the stored markdown — refetch on return.
      if (e.it.annotation_id) delete stripCache[e.it.annotation_id];
      // Coming back from the editor restores this clause/card position.
      onPersistContext?.(e.it, e.idx, e.isMatched);
      navigate(`/project/${projectId}/vijilize/${comparisonId}/annotation`);
    } catch (error) {
      console.error("Error preparing image for annotation:", error);
      toast.error("Error preparing image for annotation");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // Color boxed feature chips (.fig-ref) with their feature's hex — same
  // per-feature coding as the pills. Runs after every render (cells are
  // dangerouslySetInnerHTML, recreated on re-render); idempotent and cheap.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || columns.length === 0) return;
    const subFeatures = columns.map((c) => c.subFeature);
    root.querySelectorAll<HTMLElement>(".fig-ref").forEach((chip) => {
      // Keyed chips (data-pf-col) resolve exactly — no text matching needed
      const key = chip.getAttribute("data-pf-col");
      if (!key) pfExtendChipPrefix(chip, subFeatures);
      const hex =
        (key && columns.find((c) => c.key === key)?.hexCode) ||
        pfChipCol(chip.textContent || "", columns)?.hexCode;
      if (hex) {
        chip.style.color = hex;
        chip.style.background = `${hex}14`;
        chip.style.borderColor = `${hex}55`;
      }
    });

    // Feature / Patent Element cells: color raw occurrences of each
    // sub-feature name (unnumbered or list-numbered ones have no chip).
    const bySize = columns
      .filter((c) => c.hexCode)
      .sort((a, b) => b.subFeature.length - a.subFeature.length);
    root.querySelectorAll<HTMLElement>("[data-feat-cell]").forEach((cell) => {
      bySize.forEach((c) => {
        // First mention only, once — the effect runs after every render and
        // would otherwise color one more repeat mention per run.
        const done = Array.from(
          cell.querySelectorAll<HTMLElement>("[data-feat-colored]")
        ).some((s) => s.getAttribute("data-feat-colored") === c.subFeature);
        if (done) return;
        const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) {
          const t = walker.currentNode as Text;
          const p = t.parentElement;
          if (p && (p.closest(".fig-ref") || p.closest("[data-feat-colored]")))
            continue;
          nodes.push(t);
        }
        for (const t of nodes) {
          const text = t.textContent || "";
          const idx = text.indexOf(c.subFeature);
          if (idx < 0) continue;
          const span = document.createElement("span");
          span.setAttribute("data-feat-colored", c.subFeature);
          span.setAttribute("data-pf-col", c.key);
          span.style.color = c.hexCode!;
          span.style.fontWeight = "600";
          span.textContent = c.subFeature;
          const after = t.splitText(idx);
          after.textContent = text.slice(idx + c.subFeature.length);
          t.parentNode?.insertBefore(span, after);
          break;
        }
      });
    });
  });

  // A rotate-and-save keeps the S3 key but the browser caches the old
  // presigned URL — swap in the fresh one wherever the old one appears.
  const handleImageRotated = (newUrl: string, oldUrl: string) => {
    setColumns((prev) =>
      prev.map((c) =>
        c.patentFigureUrl === oldUrl || c.accusedImageUrl === oldUrl
          ? {
              ...c,
              patentFigureUrl:
                c.patentFigureUrl === oldUrl ? newUrl : c.patentFigureUrl,
              accusedImageUrl:
                c.accusedImageUrl === oldUrl ? newUrl : c.accusedImageUrl,
            }
          : c
      )
    );
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4 mb-4">
        {/* Spinner always at viewport center, same spot as page loads */}
        <div className="fixed inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <div className="flex gap-4 animate-pulse">
          <div className="h-64 bg-gray-200 rounded flex-1"></div>
          <div className="h-64 bg-gray-200 rounded flex-1"></div>
        </div>
      </div>
    );
  }
  if (columns.length === 0) return null;

  // Boxed feature mentions (.fig-ref chips) in the chart: hover previews that
  // feature's patent figure, click opens the full figure modal — same manner
  // as the View More page's claim chart.
  const chipColFor = (el: HTMLElement) => {
    const tagged = el.closest("[data-pf-col]") as HTMLElement | null;
    if (tagged)
      return {
        span: tagged,
        col: columns.find(
          (c) => c.key === tagged.getAttribute("data-pf-col")
        ),
      };
    const chip = el.closest(".fig-ref") as HTMLElement | null;
    if (!chip) return null;
    return { span: chip, col: pfChipCol(chip.textContent || "", columns) };
  };

  const handleMouseOver = (ev: React.MouseEvent) => {
    const t = ev.target;
    if (!(t instanceof HTMLElement)) return;
    const hit = chipColFor(t);
    if (!hit?.col?.patentFigureUrl) {
      setHoverPreview(null);
      return;
    }
    hit.span.style.cursor = "zoom-in";
    const rect = hit.span.getBoundingClientRect();
    const half = 140; // popup is 280px wide — keep it inside the viewport
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, half + 8),
      window.innerWidth - half - 8
    );
    const above = rect.bottom + 300 > window.innerHeight;
    const top = above ? rect.top - 8 : rect.bottom + 8;
    const key = hit.col.key;
    setHoverPreview((prev) =>
      prev &&
      prev.key === key &&
      prev.left === left &&
      prev.top === top &&
      prev.above === above
        ? prev
        : { key, left, top, above }
    );
  };

  // The citation sups live inside dangerouslySetInnerHTML — delegate clicks.
  const handleClick = (ev: React.MouseEvent) => {
    const t = ev.target as HTMLElement;
    if (t.classList?.contains("citation-link") && t.dataset.citationNumber) {
      onCitationClick?.(t.dataset.citationNumber);
      return;
    }
    const hit = chipColFor(t);
    if (hit?.col?.patentFigureUrl) {
      ev.preventDefault();
      setHoverPreview(null);
      setFigureModalKey(hit.col.key);
    }
  };

  const hoverCol = hoverPreview
    ? columns.find((c) => c.key === hoverPreview.key)
    : undefined;
  const figureModalCol = figureModalKey
    ? columns.find((c) => c.key === figureModalKey)
    : undefined;
  const figureModalProfiles = figureModalCol
    ? profilesForFeature(figureModalCol.subFeature)
    : [];

  // Same column placement as the View More classic chart: "Patent Figure"
  // beside "Patent Element", "Accused Image" beside "Accused Instrumentality".
  const colByKey = (key: string) => columns.find((c) => c.key === key);
  // Design runs: the chart mirrors Chart View — selected figure's rows only.
  const designMode =
    items.length > 0 && items.every((e) => e.it?.is_design_patent);
  const patentIdx = commonChart
    ? commonChart.header.findIndex((h) => /patent\s*element/i.test(h))
    : -1;
  const accusedIdx = commonChart
    ? commonChart.header.findIndex((h) => /accused\s*(?:instrumentality|implementation)/i.test(h))
    : -1;
  const anyPatentImg = columns.some((c) => c.patentFigureUrl);
  const anyAccusedImg = columns.some((c) => c.accusedImageUrl);
  const showPatentCol = patentIdx >= 0 && anyPatentImg;
  const showAccusedCol = accusedIdx >= 0 && anyAccusedImg;
  const imgCellStyle: React.CSSProperties = {
    minWidth: "14rem",
    verticalAlign: "top",
  };

  // Numbered "10. ear" list below the figure — keeps the column narrow; the
  // expanded overlay still shows image + legend side by side.
  const legendListEl = (legend: any) =>
    Array.isArray(legend) && legend.length > 0 ? (
      <ul className="mt-2 pt-2 border-t border-dashed border-gray-200 max-h-40 overflow-y-auto space-y-1 text-left [scrollbar-width:thin]">
        {legend.map((lg: any, li: number) => {
          const k = Object.keys(lg || {})[0];
          if (!k) return null;
          return (
            <li key={li} className="text-[11.5px] leading-snug text-gray-800">
              {k}. {lg[k]}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div
      ref={rootRef}
      className="mb-4"
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseLeave={() => setHoverPreview(null)}
    >
      <PerFeatureAnalysisView
        claimLabel=""
        clauseHtml={clauseHtml}
        columns={columns.map((c) => ({ ...c, isCurrent: c.key === currentKey }))}
        expandedKey={expandedKey}
        onToggleExpand={setExpandedKey}
        onSelectColumn={setCurrentKey}
        onChangePatentImage={(key) => {
          setChangeImageKey(key);
          setShowChangeImage(true);
        }}
        onAnnotate={runAnnotation}
        onReanalyze={(key) => {
          setReanalyzeKey(key);
          setShowReanalyze(true);
        }}
        patentLink={patentLink}
        targetLink={targetLink}
        onImageRotated={handleImageRotated}
      />

      {/* Common claim chart with image columns — zoom/expand/rotate per row */}
      {commonChart && (
        <div className="mt-7">
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085]">
              {designMode
                ? `Claim Chart — ${
                    colByKey(currentKey || "")?.subFeature || "Figure"
                  }`
                : "Claim Chart — All Features"}
            </p>
            <button
              type="button"
              title="View More Details"
              onClick={() =>
                openViewMoreFor(currentKey || columns[0]?.key || "", "classic")
              }
              className="walkthrough-view-more p-1 rounded border border-gray-200 bg-white shadow-sm hover:bg-gray-100 text-[#667085]"
              style={{ cursor: "pointer" }}
            >
              <OpenInFullIcon sx={{ fontSize: 14 }} />
            </button>
          </div>
          <div className="feature-chart feature-common-chart overflow-x-auto">
            <table>
              <thead>
                <tr>
                  {commonChart.header.map((h, hi) => (
                    <React.Fragment key={hi}>
                      <th dangerouslySetInnerHTML={{ __html: pfEscapeHtml(h) }} />
                      {hi === patentIdx && showPatentCol && (
                        <th style={{ minWidth: "14rem" }}>Patent Figure</th>
                      )}
                      {hi === accusedIdx && showAccusedCol && (
                        <th style={{ minWidth: "14rem" }}>Accused Image</th>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commonChart.rows
                  .filter((row) => !designMode || row.colKey === currentKey)
                  .map((row, ri) => {
                  const col = colByKey(row.colKey);
                  return (
                    <tr
                      key={ri}
                      className={
                        !designMode && row.colKey === currentKey
                          ? "pf-current"
                          : undefined
                      }
                    >
                      {row.cellsHtml.map((h, ci) => (
                        <React.Fragment key={ci}>
                          <td
                            style={{ verticalAlign: "top" }}
                            {...(ci === 0 || ci === patentIdx
                              ? { "data-feat-cell": "1" }
                              : {})}
                            dangerouslySetInnerHTML={{ __html: h }}
                          />
                          {ci === patentIdx && showPatentCol && (
                            <td style={imgCellStyle}>
                              {!row.hideImages && col?.patentFigureUrl ? (
                                <>
                                  <ExpandableZoomableImage
                                    url={col.patentFigureUrl}
                                    alt={`Patent figure — ${col.subFeature}`}
                                    title={`Patent Figure — ${col.subFeature}`}
                                    height="13rem"
                                    link={withPage(patentLink, col.figurePageNumber)}
                                    linkLabel="View Patent"
                                    legend={col.figureLegend}
                                    onRotated={handleImageRotated}
                                  />
                                  {legendListEl(col.figureLegend)}
                                </>
                              ) : null}
                            </td>
                          )}
                          {ci === accusedIdx && showAccusedCol && (
                            <td style={imgCellStyle}>
                              {!row.hideImages && col?.accusedImageUrl ? (
                                <ExpandableZoomableImage
                                  url={col.accusedImageUrl}
                                  alt={`Accused instrumentality — ${col.subFeature}`}
                                  title={`Accused Instrumentality — ${col.subFeature}`}
                                  height="13rem"
                                  link={withPage(
                                    targetLink,
                                    isPdfUrl(targetLink)
                                      ? pageFromImageUrl(col.accusedImageUrl)
                                      : null
                                  )}
                                  linkLabel="View Target"
                                  onRotated={handleImageRotated}
                                  onAnnotate={() => runAnnotation(row.colKey)}
                                />
                              ) : null}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Patent-figure preview for the hovered feature mention */}
      {hoverPreview &&
        hoverCol?.patentFigureUrl &&
        createPortal(
          <div
            className="fixed pointer-events-none"
            style={{
              left: hoverPreview.left,
              top: hoverPreview.top,
              transform: `translate(-50%, ${
                hoverPreview.above ? "-100%" : "0"
              })`,
              zIndex: 1300,
            }}
          >
            <div className="w-[280px] bg-white border border-gray-200 rounded-lg shadow-xl p-2">
              <img
                src={hoverCol.patentFigureUrl}
                alt={hoverCol.subFeature}
                className="w-full max-h-[220px] object-contain bg-[#FAFBFC] rounded"
              />
              <p className="mt-1.5 text-center text-[11px] font-interSemiBold truncate">
                <span
                  style={hoverCol.hexCode ? { color: hoverCol.hexCode } : undefined}
                >
                  {hoverCol.subFeature}
                </span>
                {hoverCol.figureNumber && (
                  <span className="text-[#667085] font-interMedium">
                    {" · "}
                    {hoverCol.figureNumber.replace(/_/g, ". ")}
                  </span>
                )}
              </p>
            </div>
          </div>,
          document.body
        )}

      {/* Full figure modal for a clicked feature mention */}
      {figureModalCol?.patentFigureUrl &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 1250 }}
            onClick={() => setFigureModalKey(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
              style={{ width: "70vw", maxWidth: "980px", maxHeight: "82vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
                <span className="text-[14px] font-interSemiBold">
                  <span
                    style={
                      figureModalCol.hexCode
                        ? { color: figureModalCol.hexCode }
                        : { color: "#1A202C" }
                    }
                  >
                    {figureModalCol.subFeature}
                  </span>
                  {figureModalCol.figureNumber && (
                    <span className="text-[#667085] font-interMedium">
                      {" · "}
                      {figureModalCol.figureNumber.replace(/_/g, ". ")}
                    </span>
                  )}
                  {patentLink && (
                    <a
                      href={withPage(patentLink, figureModalCol.figurePageNumber)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="ml-3 inline-flex items-center gap-0.5 text-[12px] font-interSemiBold text-[#1751D0] hover:underline align-middle"
                    >
                      View Patent
                      <OpenInNewIcon sx={{ fontSize: 13 }} />
                    </a>
                  )}
                </span>
                <button
                  type="button"
                  title="Close"
                  onClick={() => setFigureModalKey(null)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                  style={{ cursor: "pointer" }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0 bg-[#FAFBFC] rounded-md overflow-hidden">
                    <ZoomableImage
                      url={figureModalCol.patentFigureUrl}
                      alt={figureModalCol.subFeature}
                      height={figureModalProfiles.length > 0 ? "56vh" : "66vh"}
                    />
                  </div>
                  {figureModalCol.figureLegend &&
                    figureModalCol.figureLegend.length > 0 && (
                      <ul className="w-64 flex-shrink-0 overflow-y-auto space-y-1.5 pl-4 border-l border-dashed border-gray-200 text-left [scrollbar-width:thin]">
                        {figureModalCol.figureLegend.map((legend, index) => {
                          const k = Object.keys(legend)[0];
                          return (
                            <li
                              key={index}
                              className="text-[13px] leading-snug text-gray-800"
                            >
                              {k}. {legend[k]}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                </div>

                {/* Analyzer-built element profile(s) */}
                {figureModalProfiles.length > 0 && (
                  <div className="mt-4 border border-gray-200 rounded-lg bg-[#F8FAFC] px-4 py-3">
                    <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-2">
                      Element Profile
                    </p>
                    <div className="space-y-3">
                      {figureModalProfiles.map((profile) => (
                        <div key={profile.name}>
                          <p className="text-sm font-interSemiBold text-[#1A202C] capitalize">
                            {profile.name}
                          </p>
                          <p className="text-sm text-[#475569] leading-relaxed">
                            {profile.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* In-place ReAnalyze — same modal + API chain as View More Details */}
      {showReanalyze &&
        reanalyzeKey &&
        (() => {
          const ctx = ctxFor(reanalyzeKey);
          return (
            <ReAnalyzeModal
              isShow={showReanalyze}
              setIsShow={setShowReanalyze}
              vijilizeId={comparisonId || undefined}
              targetPatentId={ctx.targetDocId}
              projectId={projectId || undefined}
              imageS3Path={ctx.data?.markdownS3Path || undefined}
              onReanalyze={(instruction, newImageUrl) =>
                runReanalysis(reanalyzeKey, instruction, newImageUrl)
              }
            />
          );
        })()}

      {/* In-place Change Image for the patent figure */}
      {showChangeImage &&
        changeImageKey &&
        (() => {
          const ctx = ctxFor(changeImageKey);
          return (
            <ChangeVijilizeImageModal
              isShow={showChangeImage}
              setIsShow={setShowChangeImage}
              vijilizeId={comparisonId || undefined}
              folderType="figures"
              imageS3Path={ctx.srcFigS3 || undefined}
              targetPatentId={ctx.sourcePatentId}
              projectId={projectId || undefined}
              onImageChanged={(newImageUrl, previewUrl) =>
                runChangeImage(changeImageKey, newImageUrl, previewUrl)
              }
            />
          );
        })()}

      {/* API in flight — ring spinner at viewport center (loader convention) */}
      {busy &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 1400 }}
          >
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ClauseFeatureStrip;
